import Input from '../input/Input';
import useDebounce from '../../hooks/useDebounce';
import Checkbox from '../checkbox/Checkbox';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Icon } from '../icon/Icon';
import { IconTheme } from '../icon/Icon.types';
import { getClassNames } from '../../helpers/classHelpers';
import { Icons } from '../icon/icons/material';
import { TestIds } from '../../mocks/ids';
import { withUIState } from '../../contexts/ui-state/withUIState';
import { useAdminLabels } from '../../hooks/useLabels';
import styles from './ListActions.module.scss';


type OrderOptions = "desc" | "acs" | undefined;

export type ListActionType = {
    id: string;
    label: string;
    iconName: Icons;
    onClick: () => void;
    isDisabled?: boolean;
    isLoading?: boolean;
    isHidden?: boolean;
    testId?: TestIds;
};

type Props = {
    className?: string;
    isSticky?: boolean;
    isAllSelected?: boolean;
    onSelectAll?: () => void;
    onDeselectAll?: () => void;
    actions: ListActionType[];
    isDisabled?: boolean;
    isLoading?: boolean;
    onSearch?: (phrase: string | undefined) => void;
    onSort?: (order: OrderOptions) => void;
};

const DEBOUNCE_DELAY = 1000;

const ListActions: FC<Props> = ({ isSticky, className, isDisabled, isLoading, actions, isAllSelected, onDeselectAll, onSelectAll, onSearch, onSort }) => {
    const labels = useAdminLabels();
    const { searchPhrase, isSearching, onSearchChange, onSearchToggle, onSortChange, searchIcon, sortingIcon } = useDefaultListAction({ onSearch, onSort });

    return (
        <div className={getClassNames([isSticky && styles.sticky, className])}>
            <div className={styles['list-actions']}>
                {onDeselectAll && onSelectAll && <Checkbox isChecked={isAllSelected} onChange={isAllSelected ? onDeselectAll : onSelectAll} isDisabled={isDisabled || isLoading} />}

                {(onSearch || onSort) && <div className={styles['base-actions']}>
                    {onSort && <Icon.Base theme={IconTheme.inherit} size={18} iconName={sortingIcon} title={''} isClickable={!isDisabled && !isLoading} onClick={onSortChange} />}
                    {onSearch && <Icon.Base theme={IconTheme.inherit} size={18} iconName={searchIcon} title={''} isClickable={!isDisabled && !isLoading} onClick={onSearchToggle} />}
                </div>}
                <div className={styles.actions}>
                    {actions?.map((action, i) => <ListActionLabel key={i} {...action} isLoading={isLoading} isDisabled={isDisabled || action.isDisabled} />)}
                </div>
            </div>

            {onSearch && isSearching && <div className={styles['search-input']}>
                <Input placeholder={labels.searchPlaceholder} value={searchPhrase ?? ''} onChange={onSearchChange} />
            </div>}
        </div>
    );
};

export default withUIState(ListActions);


const ListActionLabel = ({ iconName, isDisabled, isLoading, isHidden, label, testId, onClick }: ListActionType) => {
    if (isHidden) return null;

    return (
        <label data-testid={testId} className={getClassNames([(isDisabled || isLoading) && styles['_disabled']])} onClick={isDisabled || isLoading ? undefined : onClick}>
            <Icon.Base theme={IconTheme.inherit} size={18} iconName={iconName} title={''} isClickable={!isDisabled && !isLoading} />
            {label}
        </label>
    );
};

const useDefaultListAction = ({ onSearch, onSort }: Pick<Props, 'onSort' | 'onSearch'>) => {
    const [isSearching, setIsSearching] = useState(false);
    const [searchPhrase, setSearchPhrase] = useState<string>();
    const [isSorting, setIsSorting] = useState<OrderOptions>();
    const debouncedSearchPhrase = useDebounce(searchPhrase, DEBOUNCE_DELAY);

    const sortingIcon = useMemo(() => isSorting === 'desc' ? Icons.sortDesc : isSorting === 'acs' ? Icons.sortAsc : Icons.sortDefault, [isSorting]);
    const onSortChange = useCallback(() => setIsSorting(s => s === 'desc' ? 'acs' : s === 'acs' ? undefined : 'desc'), []);

    useEffect(() => {
        if (!onSort) return;
        onSort(isSorting);
    }, [isSorting, onSort]);


    const searchIcon = useMemo(() => isSearching ? Icons.searchOff : Icons.search, [isSearching]);
    const onSearchChange = useCallback((phrase: string) => setSearchPhrase(phrase), []);
    const onSearchToggle = useCallback(() => {
        setIsSearching(s => {
            const val = s;
            if (val) setSearchPhrase(undefined);
            return !val;
        });
    }, []);

    useEffect(() => {
        if (!onSearch) return;
        onSearch(debouncedSearchPhrase);
    }, [debouncedSearchPhrase, onSearch]);

    return {
        onSearchChange,
        onSortChange,
        onSearchToggle,
        isSearching,
        searchIcon,
        sortingIcon,
        searchPhrase
    };
};